"use client";

import Device from "@/components/device";
import { WheelLock } from "@/components/wheel-lock";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import Image from "next/image";
import { useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect";
import { AnimatedBadge } from "@/components/motion/animated-badge";
import { cdn } from "@/lib/cdn";

type PageNarrative = {
  slideId: string;
  eyebrow: string;
  headline: string;
  description: string;
  tags: string[];
};

const NARRATIVES: PageNarrative[] = [
  {
    slideId: "demo-overview",
    eyebrow: "Page 01 — Overview",
    headline: "The whole quarter on one screen.",
    description:
      "Benchmarks and key takeaways picked from the slide, structured into modules your audience can scan in seconds.",
    tags: ["Benchmarks", "Key takeaways"],
  },
  {
    slideId: "demo-revenue",
    eyebrow: "Page 02 — Numbers",
    headline: "Charts that don't need a magnifier.",
    description:
      "Bar charts and progress bars rebuilt from your slide data — readable on the smallest phone in the room.",
    tags: ["Bar chart", "Progress"],
  },
  {
    slideId: "demo-why",
    eyebrow: "Page 03 — Comparison",
    headline: "Static decks vs. living handouts.",
    description:
      "Comparison modules turn bullet walls into structure. Quotes pull the line you want them to remember.",
    tags: ["Comparison", "Quote"],
  },
  {
    slideId: "demo-numbers",
    eyebrow: "Page 04 — Adoption",
    headline: "Tables built for thumbs.",
    description:
      "Real rows, real numbers, highlighted where it matters. No pinch-to-zoom, no rotated screens.",
    tags: ["Table", "Highlight"],
  },
  {
    slideId: "demo-feedback",
    eyebrow: "Page 05 — Feedback",
    headline: "Ask while it's still warm.",
    description:
      "A feedback module lands on the last page. Sliders for understanding, actionability, and relevance — plus an open field for the thing you can't measure.",
    tags: ["Sliders", "Free text", "Optional email"],
  },
];

const HAND_IMAGE = {
  src: cdn("homepage/livedemo/flathandholdingphone.png"),
  width: 1800,
  height: 2117,
};

const HAND_SCREEN = {
  left: 940 / 1800,
  top: 30 / 2117,
  width: 711 / 1800,
  height: 1539 / 2117,
  centerX: (940 + 711 / 2) / 1800,
};

const STAGE_WIDTH = "min(150vw, 620px)";

const SCREEN_BASE_WIDTH = 336;
const SCREEN_BASE_HEIGHT = Math.round(
  (SCREEN_BASE_WIDTH * HAND_SCREEN.height * HAND_IMAGE.height) /
    (HAND_SCREEN.width * HAND_IMAGE.width),
);

function useScreenScale() {
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useIsomorphicLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () =>
      setScale((stage.clientWidth * HAND_SCREEN.width) / SCREEN_BASE_WIDTH);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  return { stageRef, scale };
}

const NARRATIVE_BY_SLIDE = new Map(
  NARRATIVES.map((n) => [n.slideId, n] as const),
);

export const demoPages: any[] = [
  {
    slideId: "demo-overview",
    title: "Q4 Overview",
    summary: "Everything we shipped in 2025.",
    modules: [
      {
        id: "ov-benchmarks",
        type: "social-proof-stats",
        title: "Engagement",
        data: {
          stats: [
            {
              id: "reach",
              label: "Reach",
              value: "1,240",
              caption: "Unique viewers this quarter",
              visual: { kind: "spark", data: [720, 880, 940, 1050, 1120, 1180, 1240] },
            },
            {
              id: "engagement",
              label: "Engagement",
              value: "87%",
              caption: "Scroll depth beyond fold",
              visual: { kind: "bars", data: [62, 68, 74, 78, 82, 85, 87] },
            },
            {
              id: "read-time",
              label: "Avg. read time",
              value: "4 min",
              caption: "Median session duration",
              visual: { kind: "spark", data: [2.1, 2.6, 3.0, 3.4, 3.7, 3.9, 4.0] },
            },
            {
              id: "satisfaction",
              label: "Satisfaction",
              value: "94%",
              caption: "Thumbs-up reactions",
              visual: { kind: "spark", data: [82, 85, 88, 90, 92, 93, 94] },
            },
          ],
        },
      },
      {
        id: "ov-list",
        type: "list-cards",
        title: "Key takeaways",
        data: {
          cards: [
            { icon: "rocket" as const, heading: "12 features", text: "Shipped across 4 quarters" },
            { icon: "target" as const, heading: "94%", text: "Hit roadmap commitments" },
            { icon: "users" as const, heading: "1,200+", text: "Active presenters" },
          ],
        },
      },
    ],
  },
  {
    slideId: "demo-revenue",
    title: "Revenue Growth",
    summary: "Numbers that matter.",
    modules: [
      {
        id: "rev-bar",
        type: "bar",
        title: "Monthly revenue vs target",
        xAxisLabel: "Month",
        yAxisLabel: "Revenue",
        series: [
          { key: "revenue", label: "Revenue", color: "var(--chart-1)" },
          { key: "target", label: "Target", color: "var(--chart-3)" },
        ],
        showLegend: true,
        referenceArea: {
          y1: 7500,
          y2: 10000,
          label: "Stretch goal",
          variant: "pattern" as const,
          pattern: "diagonal" as const,
          color: "var(--chart-2)",
        },
        data: [
          { name: "Jan", revenue: 4200, target: 4500 },
          { name: "Feb", revenue: 4600, target: 4800 },
          { name: "Mar", revenue: 5100, target: 5000 },
          { name: "Apr", revenue: 5400, target: 5300 },
          { name: "May", revenue: 6200, target: 5700 },
          { name: "Jun", revenue: 6900, target: 6200 },
          { name: "Jul", revenue: 7100, target: 6600 },
          { name: "Aug", revenue: 7400, target: 7000 },
          { name: "Sep", revenue: 8100, target: 7500 },
          { name: "Oct", revenue: 8700, target: 8000 },
          { name: "Nov", revenue: 9200, target: 8500 },
          { name: "Dec", revenue: 9800, target: 9000 },
        ],
      },
      {
        id: "rev-progress",
        type: "progress",
        title: "Goal progress",
        data: [
          {
            value: 94,
            max: 100,
            description: "Annual revenue target",
            representation: "percentage" as const,
          },
          {
            value: 38,
            max: 40,
            description: "Enterprise deals closed",
            representation: "fraction" as const,
          },
        ],
      },
    ],
  },
  {
    slideId: "demo-why",
    title: "Why Handyout",
    summary: "Static slides vs. living handouts.",
    modules: [
      {
        id: "why-comparison",
        type: "split-comparison",
        title: "PDF vs Handyout",
        data: {
          entities: [
            { id: "pdf", name: "Static PDF" },
            { id: "hy",  name: "Handyout" },
          ],
          metrics: [
            { id: "lifespan",  label: "Lifespan",  values: { pdf: "Forgotten in 24h", hy: "Revisit anytime" }, preferred: "hy" },
            { id: "feedback",  label: "Feedback",  values: { pdf: "No feedback",      hy: "Live reactions" }, preferred: "hy" },
            { id: "direction", label: "Direction", values: { pdf: "One-way",          hy: "Two-way" }, preferred: "hy" },
          ],
        },
      },
      {
        id: "why-quote",
        type: "quote",
        title: "What they say",
        data: {
          quote: "Exactly what our audience has been asking for.",
          author: "Marie L.",
          role: "Head of Marketing",
        },
      },
    ],
  },
  {
    slideId: "demo-numbers",
    title: "By the Numbers",
    summary: "Adoption across segments.",
    modules: [
      {
        id: "num-table",
        type: "feature-comparison",
        title: "Adoption",
        data: {
          plans: [
            { name: "Users" },
            { name: "Growth", highlight: true },
          ],
          features: [
            { name: "Education", values: ["540", "+32%"] },
            { name: "Enterprise", values: ["410", "+21%"] },
            { name: "Events", values: ["250", "+48%"] },
          ],
        },
      },
    ],
  },
  {
    slideId: "demo-feedback",
    title: "How was it?",
    summary: "Tell us what landed.",
    modules: [
      {
        id: "fb-feedback",
        type: "feedback",
      },
    ],
  },
];

export function LiveDemoSection() {
  const [activeSlideId, setActiveSlideId] = useState<string>(
    NARRATIVES[0].slideId,
  );

  const narrative = NARRATIVE_BY_SLIDE.get(activeSlideId) ?? NARRATIVES[0];
  const { stageRef, scale } = useScreenScale();

  return (
    <div className="relative overflow-hidden bg-white px-4 py-12 lg:px-0">
      <div className="flex flex-col items-center text-center gap-5 ">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900/10 px-3 py-1 text-xs tracking-wider text-neutral-900">
          <Sparkles className="size-3" />
          Live demo
        </span>
        <h2 className="text-4xl font-medium text-neutral-900 sm:text-5xl md:text-6xl leading-tight">
          Your deck. Their pocket.
        </h2>
        <p className="text-lg text-neutral-600 max-w-2xl">
          Below is exactly what your audience scrolls through after the QR scan.
          AI-generated modules, real reactions — no app, no signup, no waiting.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
        <div className="relative z-10 min-h-[26rem] lg:min-h-[22rem] flex items-center pb-8 lg:pb-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={narrative.slideId}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col gap-5"
            >
              <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">{narrative.eyebrow}</AnimatedBadge>
              <h3 className="text-4xl font-medium text-neutral-900 leading-tight md:text-5xl">
                {narrative.headline}
              </h3>
              <p className="text-lg text-neutral-600 max-w-md leading-relaxed">
                {narrative.description}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {narrative.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded bg-neutral-900/10 px-2.5 py-1.5 font-mono text-xs text-neutral-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="absolute bottom-0 left-0 flex items-center gap-1.5">
            {NARRATIVES.map((n) => (
              <span
                key={n.slideId}
                className={`h-1 rounded-full transition-all duration-300 ${
                  n.slideId === activeSlideId
                    ? "w-8 bg-primary"
                    : "w-4 bg-neutral-900/20"
                }`}
              />
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="relative z-0"
          style={{
            aspectRatio: `${HAND_IMAGE.width} / ${HAND_IMAGE.height}`,
            width: STAGE_WIDTH,
            marginLeft: `calc(50% - ${STAGE_WIDTH} * ${HAND_SCREEN.centerX})`,
          }}
        >
          <div ref={stageRef} className="absolute inset-0">
            <div
              className="absolute origin-top-left overflow-hidden bg-white"
              style={{
                left: `${HAND_SCREEN.left * 100}%`,
                top: `${HAND_SCREEN.top * 100}%`,
                width: SCREEN_BASE_WIDTH,
                height: SCREEN_BASE_HEIGHT,
                transform: `scale(${scale})`,
                visibility: scale > 0 ? "visible" : "hidden",
              }}
            >
              <WheelLock className="h-full w-full">
                <Device
                  pages={demoPages}
                  mode="interactive"
                  showDeviceMockup={false}
                  autoAdvanceMs={4500}
                  onPageChange={(info) => setActiveSlideId(info.slideId)}
                />
              </WheelLock>
            </div>
            <Image
              src={HAND_IMAGE.src}
              unoptimized
              alt=""
              width={HAND_IMAGE.width}
              height={HAND_IMAGE.height}
              sizes="(min-width: 1024px) 620px, 150vw"
              className="pointer-events-none absolute inset-0 h-full w-full select-none"
              draggable={false}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 -bottom-px h-1/4 bg-gradient-to-t from-white to-transparent"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
